import { MODULE_SCOPES, type ModuleId } from "./config.ts";
import { acquireToken, getAccount } from "./broker.ts";

/**
 * Scope-set math for incremental consent (§4).
 *
 * Kept out of any one panel — originally lived in permissions-panel.ts, but
 * the setup panel needs the exact same union + probe, not a UI-specific copy
 * of it.
 */

/** Union of scopes for the given modules, de-duplicated. */
export function scopesForModules(modules: readonly ModuleId[]): string[] {
    const scopes = new Set<string>();
    for (const id of modules) {
        for (const scope of MODULE_SCOPES[id]) {
            scopes.add(scope);
        }
    }
    return [...scopes];
}

/**
 * Which of `modules` we already hold a token for.
 *
 * Probes with a silent request rather than trusting a stored flag: MSAL's
 * token cache is the real authority, and consent revoked in Entra elsewhere
 * would leave any local flag claiming access we no longer have.
 */
export async function probeGranted(modules: readonly ModuleId[]): Promise<ModuleId[]> {
    if (!getAccount()) {
        return [];
    }

    const granted: ModuleId[] = [];
    for (const id of modules) {
        try {
            await acquireToken(MODULE_SCOPES[id]);
            granted.push(id);
        } catch {
            // Not granted, or needs interaction — either way not usable silently.
        }
    }
    return granted;
}
